import axios from "axios";
import jwt_decode from "jwt-decode";
import {
  endAllSessions,
  getStoredRefreshToken,
  getStoredTokens,
  hasOtherAccounts,
  isAddingAccount,
  signOutAccount,
  withAccountLock,
} from "src/common/accountSession";
import { API_GATEWAY } from "src/constants/settings";
import { resetMixpanel } from "src/mixpanel/Mixpanel";
import { LoginCred, LogoutCred } from "src/redux/slices/credentials";
import { logoutOrganization } from "src/redux/slices/organization";
import { store } from "src/redux/store";
import { resetLocalStorage } from "./resetLocalStorage";

export const logout = () => {
  if (isAddingAccount()) return;
  if (hasOtherAccounts()) return signOutAccount(undefined, { notice: true });
  endAllSessions();
  store.dispatch(LogoutCred());
  store.dispatch(logoutOrganization());
  // shutDownIntercomm();
  resetMixpanel();
  resetLocalStorage();
  window.location.reload();
};

export let refreshingFunc: Promise<any> | undefined = undefined;

export const getToken = async () => {
  if (!refreshingFunc) refreshingFunc = ValidateAccessToken();
  let accessToken = await refreshingFunc;
  refreshingFunc = undefined;
  return `Bearer ${accessToken}`;
};

export async function PostAsync(
  baseUrl: string,
  body?: any,
  shouldUseToken = true,
  options?: any,
  isPreventLogout = false,
) {
  let headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...options?.headers,
  };

  if (shouldUseToken) {
    if (!refreshingFunc) refreshingFunc = ValidateAccessToken();
    let accessToken = await refreshingFunc;
    headers["Authorization"] = `Bearer ${accessToken}`;
    refreshingFunc = undefined;
  }
  const axiosConfig = {
    ...options,
    headers,
    ...(options?.withCredentials && { withCredentials: true }),
  };
  return await axios.post(baseUrl, body, axiosConfig).then(
    (res) => {
      return res;
    },
    (error) => {
      if (error?.response?.status === 401) {
        if (!isPreventLogout) {
          logout();
        }
      } else if (error?.response?.status === 403) {
        error.response.data = {
          Title: "Forbidden Request",
          Status: error.response.status,
          Detail: "permission_not_granted",
        };
      }
      return error.response;
    },
  );
}

export async function PostFileAsync(
  baseUrl,
  body,
  shouldUseToken = true,
  options?: any,
  isPreventLogout = false,
) {
  let headers = {
    "Content-Type": "multipart/form-data",
    Accept: "application/json",
    ...options?.headers,
  };

  if (shouldUseToken) {
    if (!refreshingFunc) refreshingFunc = ValidateAccessToken();
    let accessToken = await refreshingFunc;
    headers["Authorization"] = `Bearer ${accessToken}`;
    refreshingFunc = undefined;
  }

  const axiosConfig = {
    ...options,
    headers,
    ...(options?.withCredentials && { withCredentials: true }),
  };

  return await axios.post(baseUrl, body, axiosConfig).then(
    (res) => {
      return res;
    },
    (error) => {
      console.log(error);
      if (error?.response?.status === 401) {
        if (!isPreventLogout) {
          logout();
        }
      }
      return error.response;
    },
  );
}

export async function PutFileAsync(baseUrl, body, shouldUseToken = true) {
  let headers = {
    "Content-Type": "multipart/form-data; boundary: ",
    Accept: "application/json",
  };

  if (shouldUseToken) {
    if (!refreshingFunc) refreshingFunc = ValidateAccessToken();
    let accessToken = await refreshingFunc;
    headers["Authorization"] = `Bearer ${accessToken}`;
    refreshingFunc = undefined;
  }

  return await axios.put(baseUrl, body, { headers }).then(
    (res) => {
      return res;
    },
    (error) => {
      console.log(error);
      if (error?.response?.status === 401) {
        logout();
      }
      return error.response;
    },
  );
}

export async function GetAsync(
  baseUrl: string,
  shouldUseToken = true,
  cancelToken?: any,
  options?: any,
) {
  let headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...options?.headers,
  };
  if (shouldUseToken) {
    if (!refreshingFunc) refreshingFunc = ValidateAccessToken();
    let accessToken = await refreshingFunc;
    headers["Authorization"] = `Bearer ${accessToken}`;
    refreshingFunc = undefined;
  }

  return await axios
    .get(baseUrl, { cancelToken: cancelToken, ...options, headers })
    .then(
      (res) => {
        return res;
      },
      (error) => {
        console.log(error.message, error);
        if (error?.response && error?.response?.status === 401) {
          logout();
        }
        // Rethrow the axios error itself so error logging keeps its request config (url/method).
        if (error.message == "Network Error") {
          throw new Error("Network Error");
        }
        if (error?.response?.status === 403) {
          error.response.data = {
            Title: "Forbidden Request",
            Status: error.response.status,
            Detail: "permission_not_granted",
          };
        }
        return error.response;
      },
    );
}

export async function DeleteAsync(
  baseUrl: string,
  body?: any,
  shouldUseToken = true,
) {
  let headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (shouldUseToken) {
    if (!refreshingFunc) refreshingFunc = ValidateAccessToken();
    let accessToken = await refreshingFunc;
    headers["Authorization"] = `Bearer ${accessToken}`;
    refreshingFunc = undefined;
  }

  return await axios.delete(baseUrl, { data: body, headers: headers }).then(
    (res) => {
      return res;
    },
    (error) => {
      console.log(error);
      if (error?.response?.status === 401) {
        logout();
      } else if (error?.response?.status === 403) {
        error.response.data = {
          Title: "Forbidden Request",
          Status: error.response.status,
          Detail: "permission_not_granted",
        };
      }
      return error.response;
    },
  );
}

export async function PostWithFormDataAsyncRes(
  baseUrl,
  body,
  shouldUseToken = true,
) {
  let headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (shouldUseToken) {
    if (!refreshingFunc) refreshingFunc = ValidateAccessToken();
    let accessToken = await refreshingFunc;
    headers["Authorization"] = `Bearer ${accessToken}`;
    refreshingFunc = undefined;
  }

  return await axios.post(baseUrl, body, { headers }).then(
    (res) => {
      return res;
    },
    (error) => {
      console.log(error);
      if (error?.response?.status === 401) {
        logout();
      }
    },
  );
}

export async function PutAsync(
  baseUrl: string,
  body?: any,
  shouldUseToken = true,
) {
  let headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (shouldUseToken) {
    if (!refreshingFunc) refreshingFunc = ValidateAccessToken();
    let accessToken = await refreshingFunc;
    headers["Authorization"] = `Bearer ${accessToken}`;
    refreshingFunc = undefined;
  }

  return await axios.put(baseUrl, body, { headers }).then(
    (res) => {
      return res;
    },
    (error) => {
      if (error?.response?.status === 401) {
        logout();
      } else if (error?.response?.status === 403) {
        error.response.data = {
          Title: "Forbidden Request",
          Status: error.response.status,
          Detail: "permission_not_granted",
        };
      }
      return error.response;
    },
  );
}

export async function PatchAsync(
  baseUrl: string,
  body?: any,
  shouldUseToken = true,
) {
  let headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (shouldUseToken) {
    if (!refreshingFunc) refreshingFunc = ValidateAccessToken();
    let accessToken = await refreshingFunc;
    headers["Authorization"] = `Bearer ${accessToken}`;
    refreshingFunc = undefined;
  }

  return await axios.patch(baseUrl, body, { headers }).then(
    (res) => {
      return res;
    },
    (error) => {
      if (error?.response?.status === 401) {
        logout();
      } else if (error?.response?.status === 403) {
        error.response.data = {
          Title: "Forbidden Request",
          Status: error.response.status,
          Detail: "permission_not_granted",
        };
      }
      return error.response;
    },
  );
}

export async function PatchAsyncNew(baseUrl, body, shouldUseToken = true) {
  let headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (shouldUseToken) {
    if (!refreshingFunc) refreshingFunc = ValidateAccessToken();
    let accessToken = await refreshingFunc;
    headers["Authorization"] = `Bearer ${accessToken}`;
    refreshingFunc = undefined;
  }

  return await axios.patch(baseUrl, body, { headers }).then(
    (res) => {
      return res;
    },
    (error) => {
      if (error?.response?.status === 401) {
        logout();
      } else if (error?.response?.status === 403) {
        error.response.data = {
          Title: "Forbidden Request",
          Status: error.response.status,
          Detail: "permission_not_granted",
        };
      }
      return error.response;
    },
  );
}

export async function PostAsyncRegister(baseUrl, body, token) {
  return await axios.post(baseUrl, body).then(
    (res) => {
      return res;
    },
    (error) => {
      return error;
    },
  );
}

export async function PatchAsyncRegister(baseUrl, body) {
  return await axios.patch(baseUrl, body).then(
    (res) => {
      return res;
    },
    (error) => {
      return error.response;
    },
  );
}

export async function ValidateAccessToken() {
  const accessToken = store.getState()?.credential?.userCred.accessToken;
  // return accessToken if not expired
  if (isTokenValid()) {
    return accessToken;
  }
  return withAccountLock(async () => {
    const userCred: any = store.getState()?.credential?.userCred;
    let email = userCred?.email;
    const stored = getStoredTokens();
    if (stored) {
      store.dispatch(LoginCred({ ...userCred, ...stored }, email));
      return stored.accessToken;
    }
    const refreshTokens = [userCred?.refreshToken, getStoredRefreshToken()].filter(
      (token, i, all) => token && all.indexOf(token) === i,
    );
    if (!refreshTokens.length) return undefined;
    let rejected = false;
    for (const refreshToken of refreshTokens) {
      try {
        const response = await axios.get(
          `${API_GATEWAY}/api/userservice/Account/refreshToken/${refreshToken}`,
        );
        store.dispatch(LoginCred(response.data, email));
        return response?.data?.accessToken;
      } catch (err: any) {
        const status = err?.response?.status;
        if (status && status < 500) rejected = true;
      }
    }
    if (rejected) logout();
    return undefined;
  });
}

export function isTokenValid() {
  const accessToken = store.getState()?.credential?.userCred.accessToken;
  if (typeof accessToken === "undefined") {
    return false;
  } else {
    let decodedToken: {
      exp: number;
    } = jwt_decode(accessToken);
    let currentDate = new Date();
    if (decodedToken.exp * 1000 - 1000 * 60 * 1 < currentDate.getTime()) {
      // DeleteCookie("accessToken");
      return false;
    }
    return true;
  }
}
